/**
 * Fire-once IntersectionObserver wrapper for view/impression analytics —
 * mirrors the fire-once mechanics of the Blade lander pattern
 * (resources/views/landers/partials/lti-tracking.blade.php's thumbnail
 * IntersectionObserver), generalized for reuse across Vue components.
 *
 * Unlike the lazy-load observers already in this codebase (which re-add
 * elements as they scroll in/out), an impression should only ever fire once
 * per element's lifetime — each observe() call disconnects its own observer
 * after the callback runs.
 */
export function useImpressionTracking({ threshold = 0.5, rootMargin = '0px' } = {}) {
    /**
     * @param {Element} el
     * @param {() => void} onVisible
     */
    function observeElement(el, onVisible) {
        if (!el) return

        if (!('IntersectionObserver' in window)) {
            onVisible()
            return
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return
                observer.disconnect()
                onVisible()
            })
        }, { threshold, rootMargin })

        observer.observe(el)
    }

    return { observeElement }
}
